import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Heart, Sparkles } from "lucide-react";
import CookieConsent, { getStoredConsent, setStoredConsent } from "@/components/CookieConsent";
import { captureException } from "@/lib/sentry";
import { apiFetch } from "@/lib/apiFetch";
import { eventsUpcomingContract } from "@shared/contracts";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { useFeatureFlag } from "@/lib/FeatureFlagContext";
import { useStudioConfig } from "@/hooks/use-studio-config";

type BannerStep = "cookie" | "events" | "install" | "done";

interface UpcomingEvent {
  id: number;
  title: string;
  date: string;
  time: string;
}

function getEventDismissedKey(eventId: number) {
  return `mm_event_banner_dismissed_${eventId}`;
}

function isEventDismissed(eventId: number): boolean {
  try {
    return localStorage.getItem(getEventDismissedKey(eventId)) === "1";
  } catch {
    return false;
  }
}

function dismissEvent(eventId: number) {
  try {
    localStorage.setItem(getEventDismissedKey(eventId), "1");
  } catch {
  }
}

interface BannerSequenceProps {
  onAcceptCookies: () => void;
  onDeclineCookies: () => void;
}

export function BannerSequence({ onAcceptCookies, onDeclineCookies }: BannerSequenceProps) {
  const sequentialEnabled = useFeatureFlag("sequential_banners_enabled");
  const { config: studio } = useStudioConfig();
  const isAgency = studio.profile === "agency";

  const [step, setStep] = useState<BannerStep>(() => {
    if (getStoredConsent() === null) return "cookie";
    if (studio.profile === "agency") return "done";
    return "events";
  });

  const { data: upcomingEvents, isSuccess: eventsLoaded, isError: eventsFailed } = useQuery<UpcomingEvent[]>({
    queryKey: ["/api/events/upcoming"],
    queryFn: async () => {
      const events = await apiFetch(eventsUpcomingContract);
      return events.map((e) => ({
        id: e.id,
        title: e.title,
        date: e.date ?? "",
        time: e.time ?? "",
      }));
    },
    staleTime: 5 * 60 * 1000,
    enabled: step === "events",
  });

  const nextEvent = useMemo(() => {
    if (!upcomingEvents?.length) return null;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return upcomingEvents.find((ev) => {
      const eventDate = new Date(ev.date + "T00:00:00");
      const diff = Math.ceil((eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      return diff >= 0 && diff <= 30;
    }) ?? null;
  }, [upcomingEvents]);

  const eventDaysAway = useMemo(() => {
    if (!nextEvent) return -1;
    const eventDate = new Date(nextEvent.date + "T00:00:00");
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.ceil((eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  }, [nextEvent]);

  useEffect(() => {
    if (step !== "events") return;
    if (isAgency) {
      setStep("done");
      return;
    }
    if (eventsFailed) {
      setStep("install");
      return;
    }
    if (!eventsLoaded) return;
    if (!nextEvent || eventDaysAway < 0 || isEventDismissed(nextEvent.id)) {
      setStep("install");
    }
  }, [step, eventsLoaded, eventsFailed, nextEvent, eventDaysAway, isAgency]);

  const handleCookieAccept = () => {
    setStoredConsent("accepted", captureException);
    onAcceptCookies();
    setStep("events");
  };

  const handleCookieDecline = () => {
    setStoredConsent("declined", captureException);
    onDeclineCookies();
    setStep("events");
  };

  const handleEventDismiss = () => {
    if (nextEvent) dismissEvent(nextEvent.id);
    setStep("install");
  };

  const handleEventClick = () => {
    if (nextEvent) dismissEvent(nextEvent.id);
    setStep("install");
  };

  if (!sequentialEnabled) {
    return (
      <>
        <CookieConsent
          show={true}
          onAccept={onAcceptCookies}
          onDecline={onDeclineCookies}
        />
        {!isAgency && <PWAInstallPrompt />}
      </>
    );
  }

  if (step === "done") return null;

  return (
    <AnimatePresence mode="wait">
      {step === "cookie" && (
        <motion.div
          key="cookie"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="fixed bottom-0 left-0 right-0 z-[9999] border-t border-white/10"
          style={{ backgroundColor: "rgba(0,0,0,0.95)", backdropFilter: "blur(12px)" }}
          role="dialog"
          aria-label="Cookie consent"
          data-testid="cookie-consent-banner"
        >
          <div className="container mx-auto px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <span className="text-2xl leading-none shrink-0" aria-hidden="true">🍪</span>
            <div className="flex-1 min-w-0">
              <p className="font-subheading text-xs uppercase tracking-[0.2em] text-violet-300 mb-0.5">
                Cookie Notice
              </p>
              <p className="text-xs text-white/50 leading-relaxed">
                We use analytics cookies (Google Analytics) to understand how visitors use this site.
                No personal data is sold.{" "}
                <a
                  href="/privacy"
                  className="text-accent/80 underline underline-offset-2 hover:text-accent transition-colors"
                >
                  Privacy Policy
                </a>
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={handleCookieAccept}
                className="inline-flex items-center justify-center bg-accent hover:bg-accent/80 active:scale-[0.97] text-white uppercase tracking-widest text-xs font-bold font-subheading px-6 py-2.5 rounded-full transition-all"
                data-testid="button-cookie-accept"
              >
                Accept
              </button>
              <button
                onClick={handleCookieDecline}
                className="inline-flex items-center justify-center border border-white/20 hover:border-white/40 text-white/50 hover:text-white/80 active:scale-[0.97] uppercase tracking-widest text-xs font-semibold font-subheading px-6 py-2.5 rounded-full transition-all"
                data-testid="button-cookie-decline"
              >
                Decline
              </button>
              <button
                onClick={handleCookieDecline}
                className="w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
                aria-label="Dismiss cookie banner"
                data-testid="button-cookie-dismiss"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {step === "events" && !isAgency && nextEvent && eventDaysAway >= 0 && !isEventDismissed(nextEvent.id) && (
        <motion.div
          key="events"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="fixed bottom-0 left-0 right-0 z-[9999] border-t border-pink-500/30"
          style={{
            background: "linear-gradient(to right, rgba(67,3,39,0.95), rgba(49,10,64,0.9), rgba(38,10,56,0.95))",
            backdropFilter: "blur(12px)",
            boxShadow: "0 -4px 20px rgba(236,72,153,0.15)",
          }}
          data-testid="banner-upcoming-event"
        >
          <div className="container mx-auto px-6 py-3">
            <div className="flex items-center justify-between gap-3">
              <Link href="/events" onClick={handleEventClick} data-testid="link-event-banner" className="flex-1">
                <div className="flex items-center gap-3 cursor-pointer group">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-pink-500/20 border border-pink-400/30 flex items-center justify-center animate-pulse">
                    {nextEvent.title.toLowerCase().includes("valentine") ? (
                      <Heart size={14} className="text-pink-400" />
                    ) : (
                      <Sparkles size={14} className="text-pink-400" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-bold text-xs md:text-sm tracking-wide">
                      {nextEvent.title}
                    </span>
                    <span className="text-pink-400 text-[10px] font-subheading uppercase tracking-widest bg-pink-500/10 border border-pink-500/20 px-2 py-0.5 rounded-full">
                      {eventDaysAway === 0 ? "TODAY!" : eventDaysAway === 1 ? "TOMORROW!" : `${eventDaysAway} days away`}
                    </span>
                    <span className="text-white/60 text-xs hidden md:inline">{nextEvent.time}</span>
                  </div>
                  <ArrowRight size={14} className="text-pink-400 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                </div>
              </Link>
              <button
                onClick={handleEventDismiss}
                className="w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors shrink-0"
                aria-label="Dismiss event banner"
                data-testid="button-event-dismiss"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {step === "install" && !isAgency && (
        <PWAInstallPrompt key="install" onDone={() => setStep("done")} />
      )}
    </AnimatePresence>
  );
}
